import { createFormatAdapter, wireHelper } from '@customerio/dryad';
import type { ParseGroupHook } from '@customerio/dryad';
import { peopleFilterAttributePlugin } from './attribute-plugin';
import { peopleFilterDeviceAttributePlugin } from './device-attribute-plugin';
import { peopleFilterEventPlugin } from './event-plugin';
import { peopleFilterMessagePlugin } from './message-plugin';
import { peopleFilterNewsletterRecipientsPlugin } from './newsletter-recipients-plugin';
import { peopleFilterOptOutPlugin } from './opt-out-plugin';
import { peopleFilterSegmentsPlugin, isSegmentOrGroup } from './segments-plugin';
import { normalizeLegacyFilterWire } from './normalize-legacy-filter-wire';
import { MESSAGE_TYPE } from './named-bitmap';
import { asJsonObject } from './wire-types';
import type { JsonObject, JsonValue } from './wire-types';

export const PEOPLE_FILTER_FORMAT = 'people-filter';

/**
 * The people-search editor is a flat list with no group-nesting UI, so the
 * builder edits only the root group. The one nested wire shape — the segment
 * `{ or }` group — is claimed as a single leaf by the segments plugin, which
 * is why depth 1 suffices. Deeper groups parse to inert ErrorNodes that
 * re-emit their wire unchanged.
 */
export const PEOPLE_FILTER_MAX_DEPTH = 1;

/**
 * The people-search boundary normalizer: the builder's input, the export and
 * bulk-delete payloads, and the people list's own search query all route the
 * filters param through here. Two normalizations compose:
 *
 * - The campaign date-trigger "matching people" deep link writes the filters
 *   param as the bare legacy array wire (`[{ field, operator, type }]`), not
 *   the proto `{ and: [...] }` envelope this format parses; it converts
 *   through {@link normalizeLegacyFilterWire}.
 * - Campaign-wildcard message bitmaps whose name kept the picker-only `any_`
 *   prefix are repaired to the indexed name (see
 *   {@link repairCampaignWildcardBitmapNames}).
 *
 * An untouched wire comes back reference-identical, so callers can detect
 * "nothing to normalize" and keep their original bytes.
 */
export function normalizePeopleFilterWire(wire: JsonValue): JsonValue {
  return repairCampaignWildcardBitmapNames(normalizeLegacyFilterWire(wire));
}

// Matches only the shape the broken plugin family wrote during the AUD-4375
// window (2026-08-25/26): the codec's type prefix, then the picker value
// verbatim — `any_<channel>-campaign_<id>`, optionally with the codec's `_h`
// human suffix riding behind. The backend only ever indexes campaign
// wildcards without the prefix (`<channel>-campaign_<id>` —
// core/events/index.go in services), so a prefixed name matches nobody by
// construction and repairing it cannot collide with a real bitmap.
const BROKEN_CAMPAIGN_WILDCARD_NAME = /^[a-z]+-any_[a-z_]+-campaign_/;

// Raw atob/btoa on purpose, mirroring Ember's repairedBitmapName
// byte-for-byte (do not swap in named-bitmap.ts's UTF-8 codec): atob yields
// the name's bytes as Latin1 code units whatever encoding produced them, the
// replace strips an ASCII-only prefix, and btoa re-encodes the remaining code
// units verbatim — all inside btoa's Latin1 domain, so it cannot throw.
function repairedBitmapName(name: string): string | null {
  let decoded;
  try {
    decoded = atob(name);
  } catch {
    return null;
  }
  if (!BROKEN_CAMPAIGN_WILDCARD_NAME.test(decoded)) {
    return null;
  }
  return btoa(decoded.replace('any_', ''));
}

// Runtime-total on purpose: the wire comes off a user-editable URL param, so
// every shape assumption is guarded and anything malformed — a primitive
// node, a non-array group value, a non-record bitmap payload — passes through
// untouched rather than throwing at a read boundary.
// fallow-ignore-next-line complexity -- a flat sequence of wire shape guards; splitting hurts readability
function repairFilterChild(child: JsonValue): JsonValue {
  const node = asJsonObject(child);
  if (node == null) {
    return child;
  }
  if ('named_bitmap' in node) {
    const bitmap = asJsonObject(node.named_bitmap);
    if (bitmap == null || bitmap.type !== MESSAGE_TYPE) {
      return child;
    }
    if (typeof bitmap.name !== 'string') {
      return child;
    }
    const repaired = repairedBitmapName(bitmap.name);
    if (repaired === null) {
      return child;
    }
    return { named_bitmap: { ...bitmap, name: repaired } };
  }
  if ('not' in node) {
    const repaired = repairFilterChild(node.not);
    return repaired === node.not ? child : { not: repaired };
  }
  if (Array.isArray(node.and)) {
    const children = repairFilterChildren(node.and);
    return children === node.and ? child : { and: children };
  }
  if (Array.isArray(node.or)) {
    const children = repairFilterChildren(node.or);
    return children === node.or ? child : { or: children };
  }
  return child;
}

function repairFilterChildren(children: JsonValue[]): JsonValue[] {
  const repaired = children.map(repairFilterChild);
  const untouched = repaired.every((child, index) => child === children[index]);
  return untouched ? children : repaired;
}

/**
 * Repairs `named_bitmap` message leaves whose base64 name kept the picker-only
 * `any_` prefix — a wire only the broken AUD-4375 plugin family ever wrote.
 * The name addresses a bitmap the backend never writes, so the stored filter
 * matches nobody until its first edit re-serializes it; repairing at the read
 * boundary makes deep links written during the broken window query correctly
 * on first load. Anything unrecognized — other bitmap types, undecodable
 * names, names without the broken grammar — passes through by reference.
 */
// fallow-ignore-next-line complexity -- the same guard sequence at the typed root; splitting hurts readability
export function repairCampaignWildcardBitmapNames(wire: JsonValue): JsonValue {
  const root = asJsonObject(wire);
  if (root == null) {
    return wire;
  }
  if (Array.isArray(root.and)) {
    const children = repairFilterChildren(root.and);
    return children === root.and ? wire : { and: children };
  }
  if (Array.isArray(root.or)) {
    const children = repairFilterChildren(root.or);
    return children === root.or ? wire : { or: children };
  }
  if ('not' in root) {
    const child = repairFilterChild(root.not);
    return child === root.not ? wire : { not: child };
  }
  return wire;
}

/**
 * Narrows a decoded filters param to the proto envelope this format repairs.
 * Shared by every boundary that reads the param (the list query, the
 * bulk-delete payload, the export payload) so they recognize — and therefore
 * repair — exactly the same wires: two boundaries disagreeing about the same
 * bytes is how a list can show one audience while a mutation targets another.
 */
export function isFiltersEnvelope(decoded: JsonObject): boolean {
  return 'and' in decoded || 'or' in decoded || 'not' in decoded;
}

/**
 * dryad runs every plugin's `parse` on each candidate leaf and takes the
 * first-registered claim, so array order is claim priority. Three plugins
 * claim the `named_bitmap` leaf key: opt-out on its own bitmap type numbers
 * (listed first), message and event on disjoint decoded event types within
 * the shared message bitmap type — disjoint gates, so their relative order
 * can't change outcomes.
 */
export const peopleFilterPlugins = [
  peopleFilterOptOutPlugin,
  peopleFilterAttributePlugin,
  peopleFilterDeviceAttributePlugin,
  peopleFilterSegmentsPlugin,
  peopleFilterMessagePlugin,
  peopleFilterEventPlugin,
  peopleFilterNewsletterRecipientsPlugin,
] as const;

// fallow-ignore-next-line complexity -- a flat sequence of shape guards; splitting hurts readability
const peopleFilterParseGroup: ParseGroupHook = (json, builtInGroup) => {
  if (Array.isArray(json)) {
    return { operator: 'and', children: json };
  }
  const group = builtInGroup(json);
  // Decline the segment or-group: a null from this hook hands the node to
  // plugin claiming, so the segments plugin parses `{ or: [{ segment }…] }`
  // as one multi-select row instead of an or-group of one-segment rows.
  if (group?.operator === 'or' && isSegmentOrGroup(json)) {
    return null;
  }
  return group;
};

// No serialize hook: dryad's own walk with the default `{ [operator]:
// children }` envelope is this wire, root operator preserved and all. An
// empty tree is `{ and: [] }`.
export const peopleFilterAdapter = createFormatAdapter({
  plugins: peopleFilterPlugins,
  wire: wireHelper<JsonValue>(),
  parseGroup: peopleFilterParseGroup,
});
